import { Component } from 'react';
import PropTypes from 'prop-types';
import FormattedMessage from 'I18n/components/FormattedMessage';
import UIButton from 'UIComponents/button/UIButton';
import UILoadingButton from 'UIComponents/button/UILoadingButton';
import UIDialogBody from 'UIComponents/dialog/UIDialogBody';
import UIDialogCloseButton from 'UIComponents/dialog/UIDialogCloseButton';
import UIDialogFooter from 'UIComponents/dialog/UIDialogFooter';
import UIDialogHeader from 'UIComponents/dialog/UIDialogHeader';
import UIModalPanel from 'UIComponents/dialog/UIModalPanel';
import H2 from 'UIComponents/elements/headings/H2';
import { TeamOptionType } from '../types/OptionTypes';
import UsersAndTeamsSelector from './UsersAndTeamsSelector';
import { defaultDisallowedTeamFormattedMessage } from '../utils/TeamSelectionUtils';
import { ACCESS_LEVELS } from '../constants/AccessLevels';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
class TeamsAssetAccessPanel extends Component {
  constructor(...args) {
    super(...args);
    this.state = {
      selectedTeamIds: []
    };
    this.handleSave = () => {
      const {
        selectedTeamIds
      } = this.state;
      this.props.onSave({
        selectedTeamIds
      });
    };
  }
  renderSave() {
    const {
      showSaveLoading
    } = this.props;
    const button = /*#__PURE__*/_jsx(UILoadingButton, {
      "data-test-id": "confirm-button",
      use: "primary",
      onClick: this.handleSave,
      loading: showSaveLoading,
      children: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "ui-asset-management-lib.assetAccessPanel.buttonLabels.save"
      })
    });
    return button;
  }
  render() {
    const {
      assetTypeDisplayName,
      userHasTeamAssignmentScope,
      assignedTeamIds,
      teams,
      allowedTeamIds,
      disallowedTeamFormattedMessage,
      requestsLoading,
      requestsError,
      baseAccessValue,
      disableAccessToEveryone,
      disableAccessToEveryoneFormattedMessage
    } = this.props;
    return /*#__PURE__*/_jsxs(UIModalPanel, {
      "data-test-id": "users-and-teams-asset-access-panel",
      children: [/*#__PURE__*/_jsxs(UIDialogHeader, {
        children: [/*#__PURE__*/_jsx(UIDialogCloseButton, {
          onClick: this.props.onCancel
        }), /*#__PURE__*/_jsx(H2, {
          children: assetTypeDisplayName ? /*#__PURE__*/_jsx(FormattedMessage, {
            message: "ui-asset-management-lib.assetAccessPanel.title",
            options: {
              assetType: assetTypeDisplayName
            }
          }) : /*#__PURE__*/_jsx(FormattedMessage, {
            message: "ui-asset-management-lib.assetAccessPanel.teamsTitle"
          })
        })]
      }), /*#__PURE__*/_jsx(UIDialogBody, {
        children: /*#__PURE__*/_jsx(UsersAndTeamsSelector, {
          assignedUserIds: [],
          assignedTeamIds: assignedTeamIds,
          users: [],
          teams: teams,
          allowedTeamIds: allowedTeamIds,
          disallowedTeamFormattedMessage: disallowedTeamFormattedMessage,
          requestsLoading: requestsLoading,
          requestsError: requestsError,
          userHasTeamAssignmentScope: userHasTeamAssignmentScope,
          onUpdateUserIds: () => {},
          onUpdateTeamIds: selectedTeamIds => this.setState({
            selectedTeamIds
          }),
          fullWidth: true,
          isTeamsOnly: true,
          baseAccessValue: baseAccessValue,
          disableAccessToEveryone: disableAccessToEveryone,
          disableAccessToEveryoneFormattedMessage: disableAccessToEveryoneFormattedMessage
        })
      }), /*#__PURE__*/_jsxs(UIDialogFooter, {
        children: [this.renderSave(), /*#__PURE__*/_jsx(UIButton, {
          use: "secondary",
          onClick: this.props.onCancel,
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "ui-asset-management-lib.assetAccessPanel.buttonLabels.cancel"
          })
        })]
      })]
    });
  }
}
TeamsAssetAccessPanel.propTypes = {
  // The display name of the asset (e.g. Landing Page, Workflow, ect.)
  assetTypeDisplayName: PropTypes.string,
  // If the user can assign the asset to just themselves "Private to me.", also requires a user to assign at least one team when assigning users to an asset
  assignedTeamIds: PropTypes.arrayOf(PropTypes.number).isRequired,
  // An array of users in the portal
  teams: PropTypes.arrayOf(TeamOptionType).isRequired,
  // Optional: A list of team ids which are allowed to be selected in the tree
  allowedTeamIds: PropTypes.arrayOf(PropTypes.number),
  // Optional: The formatted message which is displayed within the tooltip on teams which are not selectable because of the allowedTeamIds. This function needs to return a FormattedMessage.
  disallowedTeamFormattedMessage: PropTypes.func,
  // On save, will dispatch an object with two arrays, an array of newly assigned users, and one of assigned teams.
  onSave: PropTypes.func.isRequired,
  // Function called if the user chooses to cancel changes, should be used to just close the panel.
  onCancel: PropTypes.func.isRequired,
  // Optional: If requests for either users or teams are loading to show a loading state
  requestsLoading: PropTypes.bool,
  // Optional: If requests for either users or teams have failed show a failure state.
  requestsError: PropTypes.bool,
  // Optional: If a save request is in progress and the panel is open, shows UILoadingDots and disables the save button to prevent multiple submissions
  showSaveLoading: PropTypes.bool,
  // ** Legacy behavior support **
  // If a user does not have "content-team-assignment" scope, but is on a team that is assigned to an asset, and that team has children
  // The user can:
  // - assign/unassign any child teams of the team they are a member of.
  // The user cannot:
  // - View the full team hierarchy
  // - Unassign their own team
  // - Change the base level of assignment of the asset
  // - Assign/unassign individual users, or view which users may already be assigned
  userHasTeamAssignmentScope: PropTypes.bool,
  // Base/default asset access level to pick for label
  baseAccessValue: PropTypes.oneOf(Object.keys(ACCESS_LEVELS)),
  // Optional: a boolean flag that disables the various "everyone" access levels when set - default false
  disableAccessToEveryone: PropTypes.bool,
  // Optional: The formatted message which is displayed within the "everyone" access levels are disabled. This function needs to return a FormattedMessage.
  disableAccessToEveryoneFormattedMessage: PropTypes.func
};
TeamsAssetAccessPanel.defaultProps = {
  requestsError: false,
  requestsLoading: false,
  showSaveLoading: false,
  userHasTeamAssignmentScope: true,
  allowedTeamIds: undefined,
  disallowedTeamFormattedMessage: defaultDisallowedTeamFormattedMessage
};
export default TeamsAssetAccessPanel;